import React from "react";
import { action, observable, reaction } from "mobx";
import Immersive3dTourPlayer from "../Immersive3dTourPlayer";
import Api from "~/service/Api";
import Immersive3dTourRepository from "~/repository/Immersive3dTour/Immersive3dTourRepository";
import JumpToView from "~/models/Immersive3dTour/JumpToView";

class Immersive3dTourPlayerStore {
  /**
   * @type {Immersive3dTourPlayer}
   */
  immersive3dTourPlayer;

  /**
   * @type {null|object}
   */
  @observable app = null;

  /**
   * @type {immersive3dTourStore|null}
   */
  @observable immersive3dTourStore = null;

  @observable isLoading = false;

  @observable isLoadingSuggestedViews = false;

  @observable isSaving = false;

  @observable message = null;

  @observable suggestedJumpToViews = [];

  @observable isFullscreen = false;

  @observable immersive3dTourRef;

  allowEdition = true;

  /**
   * @property {Immersive3dTourRepository|null}
   */
  immersive3dTourRepository = null;

  constructor(immersive3dTourStore) {
    this.immersive3dTourStore = immersive3dTourStore;
    this.immersive3dTourRef = React.createRef();
    this.immersive3dTourPlayer = new Immersive3dTourPlayer();
    this.immersive3dTourRepository = new Immersive3dTourRepository();

    reaction(
      () => this.isSaving,
      isSaving => {
        if (!this.app) {
          return;
        }

        if (!isSaving) {
          this.app.setButtonState("save", {
            caption: "Save",
            disabled: false,
          });
        } else {
          this.app.setButtonState("save", {
            caption: "Saving...",
            disabled: true,
          });
        }
      },
    );

    reaction(
      () => this.message,
      message => {
        if (!this.app) {
          return;
        }

        if (message) {
          this.app.showToast(message.header, message.type === "positive" ? "SUCCESS" : "ERROR");
        }
      },
    );
  }

  @action
  loadAiSuggestedJumpsTo() {
    if (this.isLoadingSuggestedViews) {
      return Promise.resolve();
    }
    const { immersive3dTour } = this.immersive3dTourStore;

    this.isLoadingSuggestedViews = true;

    return Api.get(`/immersive-3d-tours/${immersive3dTour.tourId}/jumps-to-suggestions`)
      .then(response => {
        this.suggestedJumpToViews.clear();

        response.data.forEach((suggestion, index) =>
          this.suggestedJumpToViews.push(new JumpToView({ id: index, ...suggestion })),
        );
      })
      .finally(() => {
        this.isLoadingSuggestedViews = false;
      });
  }

  @action
  addSuggestedJumpToView(jumpToViewId) {
    const jumpToView = this.suggestedJumpToViews.find(({ id }) => jumpToViewId === id);

    if (jumpToView.isAdded) {
      return;
    }

    jumpToView.setIsAdded(true);
    this.app.addView(jumpToView.name, jumpToView.position.id, jumpToView.position.rotation);
  }

  @action
  computeAddedSuggestedJumpToViews() {
    // set them all to false first.
    this.suggestedJumpToViews.forEach(suggestedJumpToView => suggestedJumpToView.setIsAdded(false));

    // Re-compute their 'isAdded' state.
    this.app.project.project.views.forEach(view => {
      // create an instance of JumpToView so we can have access to the computed id.
      const jumpToView = new JumpToView(view);

      this.suggestedJumpToViews.forEach(suggestedJumpToView => {
        if (suggestedJumpToView.id === jumpToView.id) {
          suggestedJumpToView.setIsAdded(true);
        }
      });
    });
  }

  @action
  save() {
    const { immersive3dTour } = this.immersive3dTourStore;
    const newProject = this.app.project;

    this.isSaving = true;

    const data = {
      project: newProject,
    };

    return this.app.saveProject(() => {
      Api.post(`/immersive-3d-tours/${immersive3dTour.tourId}/project`, data)
        .then(responseData => {
          if (responseData.status === "success") {
            this.message = {
              header: "Your Immersive 3D Tour has been saved.",
              type: "positive",
            };
          } else {
            this.message = {
              header: "An error occurred while saving your data. Please try again later.",
              type: "negative",
            };
          }
        })
        .catch(() => {
          this.message = {
            header: "An error occurred while saving your data. Please try again later.",
            type: "negative",
          };
        })
        .finally(() => {
          this.isSaving = false;
        });
    });
  }

  @action
  load(immersive3dTourEditionPlayerElement, immersive3dTourOptions) {
    const { immersive3dTour } = this.immersive3dTourStore;
    this.isLoading = true;

    return this.immersive3dTourPlayer.load().then(() => {
      this.app = window.Immersive3dTourPlayer.create(
        immersive3dTour.configuration,
        immersive3dTourEditionPlayerElement,
        {
          allowEdition: this.allowEdition,
          forceRoundDots: true,
          onReadyCallback: () => {
            this.isLoading = false;
          },
          color: "#2185D0",
          projectHotspotsUrl: `/api/immersive-3d-tours/hotspots/${immersive3dTour.tourId}`,
          ...immersive3dTourOptions,
        },
      );

      // Little Hack to allow debugging
      window.immersive3dTourApp = this.app;
    });
  }
}

export default Immersive3dTourPlayerStore;
